import type { DocumentPanelTarget } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import parseInstancePositions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/parseInstancePositions';
import type ViewerFieldIndex from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/ViewerFieldIndex';
import type { ViewerClickEvent } from '@verclam/docx-kit-editor';

/** Keys of the fields the regions around a click depend on, the innermost region's first, without duplicates. */
const resolveRegionFieldKeys = (click: ViewerClickEvent, index: ViewerFieldIndex): string[] => {
	// A dependency sits in the clicked element or in one that contains it, so the positions are tried from
	// the deepest to the outermost.
	const positions = parseInstancePositions(click.items[0]?.path);
	const fieldKeys = new Set<string>();

	for (const region of click.regions) {
		for (const reference of region.dependsOn) {
			for (let depth = positions.length; depth >= 0; depth -= 1) {
				const fieldKey = index.findFieldKey(reference.id, positions.slice(0, depth));

				if (fieldKey !== null) {
					fieldKeys.add(fieldKey);
					break;
				}
			}
		}
	}

	return Array.from(fieldKeys);
};

/**
 * What a click in the document opens, `null` when nothing Dixit knows sits under it. Priority: a field,
 * a computed field, an empty list, the text of a list element, then a conditional passage. A passage opens
 * the field it depends on when there is only one, otherwise the list of the fields it depends on (every
 * region around the click, the innermost first).
 */
const resolvePanelTarget = (click: ViewerClickEvent, index: ViewerFieldIndex): DocumentPanelTarget | null => {
	if (click.field !== undefined) {
		const fieldKey = index.findFieldKey(click.field.id, parseInstancePositions(click.occurrence?.path));

		if (fieldKey !== null) {
			return { type: 'field', fieldKey, occurrenceKey: click.occurrence?.key ?? null };
		}

		if (click.computedDependsOn !== undefined) {
			return {
				type: 'computed',
				fieldId: click.field.id,
				label: click.field.label,
				dependsOnIds: click.computedDependsOn.map((reference) => reference.id),
			};
		}
	}

	if (click.emptyList !== undefined) {
		// The nearest enclosing element holds the list: every one of its positions is a container.
		const target = index.resolveListTarget(click.emptyList.id, click.items[0]?.path, false);

		if (target !== null) {
			return { type: 'empty-list', listKey: target.list.key };
		}
	}

	if (click.item !== undefined) {
		const target = index.resolveListTarget(click.item.list.id, click.item.path, true);

		if (target !== null && target.index !== null) {
			return { type: 'list-item', listKey: target.list.key, index: target.index };
		}
	}

	if (click.regions.length > 0) {
		const fieldKeys = resolveRegionFieldKeys(click, index);
		const [onlyFieldKey] = fieldKeys;

		if (fieldKeys.length === 1 && onlyFieldKey !== undefined) {
			return { type: 'field', fieldKey: onlyFieldKey, occurrenceKey: null };
		}

		return { type: 'condition', fieldKeys };
	}

	return null;
};

export default resolvePanelTarget;
